import { MEXICAN_STATES } from './constants'
import { LocaleConfig } from '@/config/types'
import { MexicanAddress } from '@/config/mx/types'
import { Country } from '@/i18n/routing'
import { safeStringTrim } from '@/utils/safe-trim'

export const mxConfig: LocaleConfig = {
  name: 'México',
  country: 'MX',
  coordinates: {
    lat: 19.40801533265007,
    lng: -99.18341727357178
  },
  timezone: 'America/Mexico_City',
  phoneFormat: '+52 XXX XXX XXXX',
  dateFormat: 'dd/MM/yyyy',
  flag: '/mexico.png',
  mainPoliticalDivisions: MEXICAN_STATES,
  zoomLevels: {
    country: 5,
    veryLarge: 6,
    large: 8,
    medium: 10,
    small: 12,
    verySmall: 14,
    default: 5
  },
  radiusConfig: {
    country: 100000,
    administrativeArea: 50000,
    locality: 15000,
    neighborhood: 3000,
    route: 1500,
    intersection: 300,
    premise: 150,
    pointOfInterest: 800
  },
  addressConfig: {
    mainPoliticalDivisionField: 'state',
    country: Country.MX,
    formatter: (data: MexicanAddress) => {
      const parts: string[] = []

      const streetName = safeStringTrim(data.streetName)
      const externalNumber = safeStringTrim(data.externalNumber)

      if (streetName && externalNumber) {
        parts.push(`${streetName} ${externalNumber}`)
      } else if (streetName) {
        parts.push(streetName)
      }

      const internalNumber = safeStringTrim(data.internalNumber)
      if (internalNumber) {
        parts.push(internalNumber)
      }

      const colonia = safeStringTrim(data.colonia)
      if (colonia) {
        parts.push(colonia)
      }

      const municipality = safeStringTrim(data.municipality)
      if (municipality) {
        parts.push(municipality)
      }

      const city = safeStringTrim(data.city)
      if (city) {
        parts.push(city)
      }

      const state = safeStringTrim(data.state)
      if (state) {
        parts.push(state)
      }

      const postalCode = safeStringTrim(data.postalCode)
      if (postalCode) {
        parts.push(postalCode)
      }

      return parts.join(', ')
    },

    parser: (legacyData: any): MexicanAddress => {
      console.log('🔍 Parser input:', JSON.stringify(legacyData, null, 2))

      const street = safeStringTrim(legacyData.street)
      let streetName = ''
      let externalNumber = ''

      if (street) {
        const words = street.split(' ')
        const lastWord = words[words.length - 1]

        if (/^\d+[A-Za-z]*$/.test(lastWord)) {
          streetName = words.slice(0, -1).join(' ')
          externalNumber = lastWord
        } else {
          streetName = street
          externalNumber = ''
        }
      }

      const result: MexicanAddress = {
        streetName: streetName,
        externalNumber: externalNumber,
        internalNumber: '',
        colonia: safeStringTrim(legacyData.neighborhood),
        municipality: safeStringTrim(legacyData.city),
        city: safeStringTrim(legacyData.city),
        state: safeStringTrim(legacyData.state),
        postalCode: safeStringTrim(legacyData.postalCode)
      }

      console.log('🔄 Parser output:', JSON.stringify(result, null, 2))
      return result
    },

    validator: (data: MexicanAddress) => {
      const errors: string[] = []

      const city = safeStringTrim(data.city)
      if (!city) {
        errors.push('Ciudad es requerida')
      }

      const state = safeStringTrim(data.state)
      if (!state) {
        errors.push('Estado es requerido')
      }

      return { valid: errors.length === 0, errors }
    },

    searchableFields: [
      { key: 'colonia', label: 'Colonia', mongoPath: 'address.data.colonia' },
      { key: 'city', label: 'Ciudad', mongoPath: 'address.data.city' },
      { key: 'state', label: 'Estado', mongoPath: 'address.data.state' },
      { key: 'postalCode', label: 'Código Postal', mongoPath: 'address.data.postalCode' }
    ],

    excelMapping: {
      streetName: {
        label: 'Calle',
        dbField: 'address.data.streetName'
      },
      externalNumber: {
        label: 'Número Exterior',
        dbField: 'address.data.externalNumber'
      },
      internalNumber: {
        label: 'Número Interior',
        dbField: 'address.data.internalNumber'
      },
      colonia: {
        label: 'Colonia',
        dbField: 'address.data.colonia'
      },
      municipality: {
        label: 'Municipio',
        dbField: 'address.data.municipality'
      },
      city: {
        label: 'Ciudad',
        dbField: 'address.data.city'
      },
      state: {
        label: 'Estado',
        dbField: 'address.data.state'
      },
      postalCode: {
        label: 'CP',
        dbField: 'address.data.postalCode'
      }
    }
  }
}
